import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import { getAllFeatureConfigs } from '../config/featureDisplayConfig';
import { DashboardAPI } from '../services/feature-api';

// Màn hình chính — bố cục 6 nhóm chép từ PE_Form/Dashboard.js (sơ đồ "Hệ sinh thái toàn diện" anh Bình 23/09/2026).
// THM giai đoạn 1 chỉ nhóm 1 có tính năng; nhóm trống không hiện. Màu tạm theo Yody (QD-08).
const NHOM = {
  1: { ten: 'Quản trị bản thân', mau: '#0F766E' },
  2: { ten: 'Quản trị đội nhóm', mau: '#1D4ED8' },
  3: { ten: 'Kinh doanh', mau: '#B45309' },
  4: { ten: 'Thi đua', mau: '#BE185D' },
  5: { ten: 'Học tập, rèn luyện', mau: '#6D28D9' },
  6: { ten: 'Thành quả', mau: '#C2410C' },
};

const sang = (hex, p) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (c) => Math.round(c + (255 - c) * p);
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
};

const Dashboard = ({ userInfo, onLogout, accessToken }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});

  const loadStats = useCallback(async () => {
    if (!accessToken) return;
    const now = new Date();
    const r = await DashboardAPI.getStats(accessToken, now.getMonth() + 1, now.getFullYear());
    if (r.success && r.data) setStats(r.data);
  }, [accessToken]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const items = getAllFeatureConfigs().filter((f) => f.enabled !== false).sort((a, b) => (a.order || 99) - (b.order || 99));

  const statText = (item) => {
    if (!item.dashboardStat?.enabled) return null;
    const val = stats[item.key]?.[item.dashboardStat.statKey || 'total'];
    return val == null ? null : `${Number(val).toLocaleString('vi-VN')} ${item.dashboardStat.label || ''}`.trim();
  };

  const O = ({ item, mau }) => (
    <div className="flex flex-col items-center gap-1.5 cursor-pointer group" onClick={() => navigate(item.path)} style={{ WebkitTapHighlightColor: 'transparent' }}>
      <div className="relative">
        <div className="w-[60px] h-[60px] rounded-[19px] flex items-center justify-center relative overflow-hidden transition-transform duration-150 group-active:scale-90 text-white"
          style={{ background: `linear-gradient(145deg, ${sang(mau, 0.28)}, ${mau})`, boxShadow: `0 8px 18px -8px ${mau}, inset 0 1px 0 rgba(255,255,255,.35)` }}>
          <span className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,.22), transparent 55%)' }} />
          <span className="material-symbols-rounded relative" style={{ fontSize: 30, filter: 'drop-shadow(0 2px 2px rgba(0,0,0,.18))' }}>{item.icon}</span>
        </div>
        <span className="absolute -top-[7px] -left-[7px] min-w-[21px] h-[21px] px-1 rounded-full bg-white text-[10.5px] font-extrabold flex items-center justify-center shadow-md border-[1.5px]" style={{ color: mau, borderColor: mau }}>
          {item.order}
        </span>
      </div>
      <span className="text-[11px] font-semibold text-center leading-tight max-w-[82px] text-gray-800">{item.name}</span>
      {statText(item) && <span className="text-[9.5px] font-bold rounded-full px-2 py-0.5 -mt-0.5" style={{ backgroundColor: `${mau}1F`, color: mau }}>{statText(item)}</span>}
    </div>
  );

  const userName = userInfo?.facebook || userInfo?.name || 'Bạn';

  return (
    <div className="min-h-screen bg-[#F4F6F3] font-['Inter'] text-gray-900 pb-8">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} pageTitle="Màn hình chính" />
      <div className="px-4 py-3 flex items-center gap-3 relative overflow-hidden"
        style={{ background: 'radial-gradient(120% 140% at 0% 0%, #1E5A35 0%, #0F3D2A 55%, #0A2B1D 100%)', borderRadius: '0 0 1.6rem 1.6rem' }}>
        <div className="absolute -top-24 -right-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(231,196,106,.35), transparent 60%)', filter: 'blur(10px)' }} />
        <img alt="" className="w-11 h-11 rounded-full border-2 border-white/30 object-cover relative" src={userInfo?.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(userName)} />
        <div className="flex-1 min-w-0 relative">
          <p className="text-white/60 text-[10px] font-medium uppercase tracking-[.14em]">Xin chào</p>
          <h1 className="text-white text-base font-bold truncate">{userName}</h1>
        </div>
      </div>

      <main className="px-4 mt-3.5 space-y-3.5">
        {Object.entries(NHOM).map(([stt, g]) => {
          const cua = items.filter((f) => String(f.nhom) === stt);
          if (!cua.length) return null;
          return (
            <section key={stt} className="bg-white rounded-[22px] px-3 pt-3.5 pb-3 border border-[#ECEFEC]" style={{ boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 10px 24px -18px rgba(0,0,0,.25)' }}>
              <div className="flex items-center gap-2.5 mx-1 mb-3">
                <span className="w-[30px] h-[30px] rounded-[10px] flex items-center justify-center text-white font-extrabold text-[13px] flex-shrink-0" style={{ backgroundColor: g.mau }}>{stt}</span>
                <h2 className="text-[14.5px] font-bold flex-1 tracking-tight">{g.ten}</h2>
                <span className="text-[11px] text-gray-500 font-semibold">{cua.length} mục</span>
              </div>
              <div className="grid grid-cols-4 gap-y-3 gap-x-1.5">{cua.map((it) => <O key={it.key} item={it} mau={g.mau} />)}</div>
            </section>
          );
        })}
      </main>
    </div>
  );
};

export default Dashboard;
